import { useEffect, useRef } from "react";
import { nowHM, todayStr, fmt12 } from "@/lib/api";

export const NOTIFY_KEY = "pillbox_reminders";

export const remindersOn = () => localStorage.getItem(NOTIFY_KEY) === "on";

export const notificationState = () => {
  if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) return "unsupported";
  return Notification.permission; // default | granted | denied
};

export const registerReminderWorker = async () => {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch {
    return null;
  }
};

export const enableReminders = async () => {
  if (typeof Notification === "undefined") return "unsupported";
  await registerReminderWorker();
  if (Notification.permission === "denied") return "denied";
  const res = await Notification.requestPermission();
  const final = res === "default" ? Notification.permission : res;
  localStorage.setItem(NOTIFY_KEY, final === "granted" ? "on" : "off");
  return final === "default" ? "denied" : final;
};

export const disableReminders = () => localStorage.setItem(NOTIFY_KEY, "off");

// Android Chrome refuses `new Notification()` — it requires the service worker registration.
const show = async (title, options) => {
  try {
    const reg = (await navigator.serviceWorker?.getRegistration()) || (await registerReminderWorker());
    if (reg?.showNotification) return reg.showNotification(title, options);
    return new Notification(title, options);
  } catch {
    /* ignore */
  }
};

export const testReminder = () =>
  show("Pillbox reminders are on", { body: "This is how a dose reminder looks.", tag: "pillbox-test" });

export const useReminders = (items, date) => {
  const sent = useRef(new Set());

  useEffect(() => {
    if (date !== todayStr() || !remindersOn() || notificationState() !== "granted") return;
    const check = () => {
      const now = nowHM();
      (items || []).forEach((i) => {
        if (i.status === "taken" || i.status === "skipped") return;
        const due = i.snooze_until || i.time;
        const key = `${date}|${i.med_id}|${i.time}|${due}`;
        if (now >= due && !sent.current.has(key)) {
          sent.current.add(key);
          show(`Time for ${i.name}`, { body: `Due ${fmt12(due)} — mark it taken or skipped`, tag: key });
        }
      });
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [items, date]);
};
